/**
 * @file    CprContext.tsx
 * @scope   STATE
 * @summary Drawer state for the Cross-Program Rollup subtype (composed engine reducer, §9).
 * @consumes composeEngineReducer, CPR_DESCRIPTOR
 * @produces CprProvider, useCprContext, CprContextValue
 * @sideEffects Calls `onStateChange` with the CPR slice after every change (store mirroring).
 * @notes   Mounted next to `EnterpriseProvider` by the DiyDrawer wrapper. The default context value
 *          is a safe no-op so components rendered outside the provider never crash.
 *
 * TODO(ai): this is a PER-SUBTYPE provider sitting beside `EnterpriseProvider`. Replace both with a
 * single subtype-generic engine provider (state typed by the active descriptor) once a third
 * subtype appears; the drawer seam `useActiveEngineSlice` is the only place that picks between them.
 */
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import {
  EngineCoreAction,
  composeEngineReducer,
  createEngineState,
} from '../../../engine/state/composeEngineReducer';
import { CPR_DESCRIPTOR } from '../descriptor';
import { CprSlice } from '../model';
import { CprAction, CPR_INITIAL_STATE } from './cprReducer';
import { isCprInvalid } from '../validation';

const cprEngineReducer = composeEngineReducer<CprSlice, CprAction>(CPR_DESCRIPTOR);

export type CprContextValue = {
  state: CprSlice;
  dispatch: (action: CprAction | EngineCoreAction) => void;
  isInvalid: () => boolean;
};

const noop = () => undefined;

const CprContext = createContext<CprContextValue>({
  state: CPR_INITIAL_STATE,
  dispatch: noop,
  isInvalid: () => true,
});

type CprProviderProps = {
  children: React.ReactNode;
  initialState?: CprSlice;
  onStateChange?: (slice: CprSlice) => void;
};

export const CprProvider: React.FC<CprProviderProps> = ({
  children,
  initialState,
  onStateChange,
}) => {
  const [engineState, dispatch] = useReducer(
    cprEngineReducer,
    createEngineState(CPR_DESCRIPTOR.id, initialState ?? CPR_INITIAL_STATE)
  );
  const slice = engineState.slice;

  useEffect(() => {
    onStateChange?.(slice);
  }, [slice]);

  const value = useMemo<CprContextValue>(
    () => ({ state: slice, dispatch, isInvalid: () => isCprInvalid(slice) }),
    [slice]
  );

  return <CprContext.Provider value={value}>{children}</CprContext.Provider>;
};

export const useCprContext = (): CprContextValue => useContext(CprContext);
